/**
 * Text panel - Figma `6377:87716` (1360x371).
 *
 * One large paragraph in a coloured panel and, when the editor gives it a label, one button
 * at the foot. Measured from the file: padding 36, the paragraph 968 wide at h4 (26px over
 * 34), the button pinned to the bottom edge, the panel's own colour `#f1f1f1` - `grey-200`,
 * which is `surface-sunken` and not the `surface-muted` a card uses.
 *
 * **The colour is a field, and the text colour follows it.** Each tone carries the text
 * colour it is drawn in, the same pairs `pillars` takes from the design, so an editor
 * cannot choose a panel nobody can read. Grey is the drawing; the others are the palette
 * the file already uses for coloured cards.
 *
 * It is not a variant of `promo-banner`: that section requires a heading, draws a white
 * card inside a frame, and this design has neither.
 */
import { CtaLink, SectionShell } from '../primitives';
import type { TextPanelSection } from '../../types/sections';

const TONES: Record<string, string> = {
  grey: 'bg-grey-200 text-ink-strong',
  white: 'bg-surface text-ink-strong border border-ink/10',
  green: 'bg-green-500 text-green-900',
  blue: 'bg-blue-200 text-blue-dark-400',
  black: 'bg-black text-white',
  orange: 'bg-orange-200 text-orange-800'
};

export default function TextPanel({ data }: { data: TextPanelSection }) {
  const tone = TONES[data.tone ?? 'grey'] ?? TONES.grey;

  return (
    <SectionShell settings={data.settings} name="text-panel">
      <div className={`flex min-h-[371px] flex-col justify-between gap-16 rounded-m p-6 lg:p-9 ${tone}`}>
        <p className="max-w-[968px] whitespace-pre-line text-h4">{data.body}</p>
        {data.cta?.label ? <CtaLink value={data.cta} /> : null}
      </div>
    </SectionShell>
  );
}
